import { watchAccessLine } from './access-line'
import { inStoreApp } from './store-app'
import { bindRoundSwitcher } from './round-switcher'
import { installBrandedSelects } from './branded-select'
import './styles.css'
import './motion.css'
import { startPickedChips } from './picked-chips'
import './navigation.css'
import { desktopDestination, DOLLAR_ICON, mobileDestination, moreBackLink, ownerNavHTML, OWNER_DESTINATIONS, rememberCameFrom, type CameFrom } from './navigation'
import { mountMore } from './screens/more'
import { invoicesOnDataChanged, mountInvoices } from './screens/invoices'
import { mountOldCustomers, oldCustomersOnDataChanged } from './screens/old-customers'
import { ownerPagePresentation } from './route-presentation'
import './followup.css'
import './brought.css'
import { BEHIND_EARLIER, SIGNED_OUT_MESSAGE, api, cancelQueued, dismiss, getQueue, onQueueChange, queuePersistenceOk, waitingForOtherAccounts } from './api'
import { chipItemState, chipSummary, labelWords, noticeLines } from './queue-chip'
import { bumpRouteGen, routeGen } from './route-state'
import { el, money, nextBookedMessage, PRIVACY_URL, shortDate, toast, todayISO } from './ui'
import { gridBusy, gridComeBack, gridOnDataChanged, mountGrid } from './screens/grid'
import { mountNewJobs, newJobsOnDataChanged } from './screens/new-jobs'
import { customerOnDataChanged, mountCustomer, noteScreenBeforeCustomer } from './screens/customer'
import { mountSchedule, noteScreenBeforeDiary, scheduleBusy, scheduleComeBack, scheduleOnDataChanged } from './screens/schedule'
import { mountOwed, owedOnDataChanged } from './screens/owed'
import { nextOwedBadgeSequence, onOwedBadgeReread, setOwedBadge } from './owed-badge'
import { watchEnquiryBadge } from './enquiry-alerts'
import type { OwedPayload } from './owed-view'
import { mountRebook, rebookBusy, rebookComeBack, rebookOnDataChanged, refreshRebookBadge } from './screens/rebook'
import { readAgainOnComeBack } from './come-back'
import { chatsOnDataChanged, mountChats } from './screens/chats'
import { mountGetSetUp } from './screens/get-set-up'
import { historyOnDataChanged, mountHistory } from './screens/history'
import { mountRaisePrices, raisePricesOnDataChanged } from './screens/raise-prices'
import { analysisOnDataChanged, mountAnalysis } from './screens/analysis'
import { closeOutOnDataChanged, mountCloseOut } from './close-out'
import { bootScreen, getMe, isWorker, loadMe, ownerDoesJobs } from './me'
import { workToday } from './open-on-today'
import { startAutoSend } from './media-store'
import { enablePullToRefresh } from './pull-refresh'
import { watchSheets } from './sheet-close'
import { keepEveryDialogAboveKeyboard } from './sheet-keyboard'
import { mountToday, todayOnDataChanged } from './screens/today'
import { mountMyDays } from './screens/my-days'
import { mountMap, unmountMap } from './screens/map'
import { businessOnDataChanged, mountBusiness } from './screens/business'
import { mountPrices, pricesOnDataChanged } from './screens/prices'
import { mountPlan } from './screens/plan'
import { mountAccount } from './screens/account'
import { extraFeaturesOnDataChanged, mountExtraFeatures } from './screens/extra-features'
import { mountPeople, peopleOnDataChanged } from './screens/people'
import { mountCleaners, cleanersOnDataChanged } from './screens/cleaners'
import { mountPartners } from './screens/partners'
import { mountYourMessages } from './screens/your-messages'
import { mountSetup } from './screens/setup'
import { rememberSignupFrom } from './signup-from'
import { currencySymbol, setRoundConfig } from './round-config'
import { logOut, mountSignIn } from './screens/signin'
import { forgetAppReminders, watchAppReminders } from './reminders'
import { forgetAppCard, watchAppCard } from './lock-card'
import { watchCalls } from './calls'
import { mountImport } from './screens/import'
import { mountBank } from './screens/bank'
import { mountStart } from './screens/start'
import { mountPaused } from './screens/paused'
import { chooseAndUploadAvatar, removeAvatar } from './avatar'
import { bindHelpButton } from './help'
import './help.css'
// Underlines only on links, one look for small buttons (28 Oct 2026): after every screen's own styles.
import './book-again.css'
import './up-next.css'
import './links.css'
// A job made with no signal books its first visit once it saves (several jobs): listening from the start.
import './pending-jobs'
// A message about several jobs learns each of its saves as they land, so its Undo can reverse them all.
import './message-records'

// Shell + hash router + the retry-queue chip. Owner screens (crew K5):
//   #/schedule    — the day view + close-out (default: the van lands here)
//   #/grid        — the Work list replacement (spreadsheet)
//   #/new-jobs    — the workbook's New jobs (leads) tab
//   #/owed        — the receivable to chase
//   #/rebook      — overdue customers with no agreed visit
//   #/analysis    — the round's money and health, read-only
//   #/history     — read-only audit of every change
//   #/map/date    — one day's stops as a map, in a tab of its own
//   #/customer/id — detail + log-a-clean
//   #/business    — the round's own name, signoffs and bank lines
//   #/extra-features — optional tools (several jobs), reached from Your business and More
//   #/people      — who can sign in to this round
// WORKER sign-ins get #/today and #/my-days (the days they book, 0040), and are
// redirected to Today from every other route — backed by the server-side scope
// in _middleware.
// ADMIN sign-ins get #/rounds (was #/cleaners) for businesses and
// #/partners for creator applications. Other business screens stay out of the admin shell.
// 'none' (not on any list) and a suspended business never
// reach the router at all — boot() takes those over before route() runs.

type Route = {
  screen:
    | 'more'
    | 'invoices'
    | 'old-customers'
    | 'grid'
    | 'customer'
    | 'schedule'
    | 'owed'
    | 'rebook'
    | 'chats'
    | 'get-set-up'
    | 'new-jobs'
    | 'history'
    | 'analysis'
    | 'raise-prices'
    | 'today'
    | 'my-days'
    | 'map'
    | 'close-out'
    | 'business'
    | 'prices'
    | 'plan'
    | 'account'
    | 'extra-features'
    | 'your-messages'
    | 'people'
    | 'cleaners'
    | 'partners'
    | 'import'
    | 'bank'
    | 'start'
  id?: number
  date?: string
}

function parseHash(): Route {
  const h = window.location.hash
  const m = h.match(/^#\/customer\/(\d+)/)
  if (m) return { screen: 'customer', id: parseInt(m[1], 10) }
  const day = h.match(/^#\/map\/(\d{4}-\d{2}-\d{2})/)
  if (day) return { screen: 'map', date: day[1] }
  const closeDay = h.match(/^#\/close-out\/(\d{4}-\d{2}-\d{2})/) // Close out the day (0095, 29 Oct 2026)
  if (closeDay) return { screen: 'close-out', date: closeDay[1] }
  if (h.startsWith('#/more')) return { screen: 'more' }
  if (/^#\/invoices(?:[/?]|$)/.test(h)) return { screen: 'invoices' } // More > Invoices (22 Oct 2026)
  if (h.startsWith('#/old-customers')) return { screen: 'old-customers' } // More > Old customers (26 Oct 2026)
  if (h.startsWith('#/today')) return { screen: 'today' }
  if (h.startsWith('#/my-days')) return { screen: 'my-days' }
  if (h.startsWith('#/grid')) return { screen: 'grid' }
  if (h.startsWith('#/new-jobs')) return { screen: 'new-jobs' }
  if (h.startsWith('#/owed')) return { screen: 'owed' }
  if (h.startsWith('#/rebook')) return { screen: 'rebook' }
  if (h.startsWith('#/chats')) return { screen: 'chats' } // Messages' Chats (0060)
  if (h.startsWith('#/get-set-up')) return { screen: 'get-set-up' } // More > Get set up (10 Oct 2026)
  if (h.startsWith('#/history')) return { screen: 'history' }
  if (h.startsWith('#/raise-prices')) return { screen: 'raise-prices' }
  if (h.startsWith('#/analysis') || h.startsWith('#/money')) return { screen: 'analysis' }
  if (h.startsWith('#/business')) return { screen: 'business' }
  if (h.startsWith('#/prices')) return { screen: 'prices' } // Your prices (0037), reached from Your business
  if (h.startsWith('#/account')) return { screen: 'account' } // Your account (4 Nov 2026), More and the account menu
  if (h.startsWith('#/plan')) return { screen: 'plan' } // Your plan (billing stage 2), More > Your business
  if (h.startsWith('#/extra-features')) return { screen: 'extra-features' } // also reached from the owner More hub
  if (h.startsWith('#/your-messages')) return { screen: 'your-messages' } // More > Your messages (8 Oct 2026)
  if (h.startsWith('#/people')) return { screen: 'people' }
  // The admin's list of every round (Liam 2026-09-27: "It's not just cleaners, it's anyone with a
  // round"). #/cleaners was its first address and still opens it.
  if (/^#\/partners(?:[/?]|$)/.test(h)) return { screen: 'partners' }
  if (h.startsWith('#/rounds') || h.startsWith('#/cleaners')) return { screen: 'cleaners' }
  if (h.startsWith('#/import')) return { screen: 'import' }
  if (h.startsWith('#/bank')) return { screen: 'bank' }
  if (h.startsWith('#/start')) return { screen: 'start' }
  return { screen: 'schedule' }
}

// Multi-business (2026-09-22): who is signed in decides the shell, on top of
// the existing worker/owner split. Read defensively — getMe() only widens
// to admin/suspended once the loader (me.ts, W3) can tell those states
// apart from "unknown"; see the manager report in .worker-done-w5.md.
function isAdmin(): boolean {
  return getMe()?.role === 'admin'
}

function isSuspended(): boolean {
  const me = getMe() as unknown as { suspended?: boolean } | null
  return me?.suspended === true
}

let current: Route = parseHash()

function renderNav(): void {
  const phone = window.matchMedia('(max-width: 760px)').matches
  const owner = document.body.classList.contains('owner-shell')
  const topbar = owner ? document.querySelector<HTMLElement>('.topbar') : null
  if (topbar) document.body.style.setProperty('--roundly-topbar-height', `${Math.ceil(topbar.getBoundingClientRect().height)}px`)
  const ownerNav = document.querySelector<HTMLElement>('.owner-nav')
  if (ownerNav) {
    const links = [...ownerNav.querySelectorAll<HTMLAnchorElement>('.nav-item')]
    const order = phone ? ['schedule', 'today', 'rebook', 'owed', 'more', ...OWNER_DESTINATIONS.filter(d => !('mobile' in d)).map(d => d.screen)] : [...OWNER_DESTINATIONS.map(d => d.screen), 'more']
    const sorted = [...links].sort((a, b) => order.indexOf(a.dataset.screen!) - order.indexOf(b.dataset.screen!))
    if (links.some((a, i) => a !== sorted[i])) {
      const focus = document.activeElement instanceof HTMLElement && ownerNav.contains(document.activeElement) ? document.activeElement : null
      ownerNav.append(...sorted)
      focus?.focus({ preventScroll: true })
    }
  }
  const selected = owner ? (phone ? mobileDestination(current.screen) : desktopDestination(current.screen)) : current.screen
  for (const a of document.querySelectorAll<HTMLAnchorElement>('.nav-item')) {
    const on = a.dataset.screen === selected
    a.classList.toggle('nav-active', on)
    if (on) a.setAttribute('aria-current', 'page')
    else a.removeAttribute('aria-current')
  }
  // The top-bar Chats button shows chosen while you are on Chats, like a tab (Liam, 2 Nov 2026).
  const chats = document.querySelector<HTMLElement>('.messages-chats-button')
  if (chats) {
    chats.classList.toggle('is-here', current.screen === 'chats')
    if (current.screen === 'chats') chats.setAttribute('aria-current', 'page'); else chats.removeAttribute('aria-current')
  }
  const nav = document.querySelector<HTMLElement>('.owner-nav')
  const active = nav?.querySelector<HTMLElement>('.nav-active')
  if (nav && active && active.getBoundingClientRect().width) {
    const n = nav.getBoundingClientRect(), r = active.getBoundingClientRect()
    nav.style.setProperty('--indicator-x', `${r.left - n.left}px`)
    nav.style.setProperty('--indicator-y', `${r.top - n.top}px`)
    nav.style.setProperty('--indicator-width', `${r.width}px`)
    nav.style.setProperty('--indicator-height', `${r.height}px`)
  } else nav?.style.setProperty('--indicator-width', '0px')
}
window.addEventListener('resize', renderNav)
// An iPhone can leave the header and the bottom tab bar where they sat while the keyboard was up, so the tab bar shows
// halfway up the screen until the page scrolls (Liam's phone, 9 Oct 2026). When the keyboard closes (the visible part
// of the page grows back by more than a keyboard's worth and no box is being typed in), the page is nudged one pixel
// and back, which makes the phone put them back. Nothing else moves.
if (window.visualViewport) {
  const vv = window.visualViewport
  let lastHeight = vv.height
  vv.addEventListener('resize', () => {
    const grew = vv.height - lastHeight
    lastHeight = vv.height
    const typing = document.activeElement instanceof HTMLElement && document.activeElement.matches('input, textarea, select, [contenteditable="true"]')
    if (grew > 150 && !typing) requestAnimationFrame(() => { window.scrollBy(0, 1); window.scrollBy(0, -1) })
  })
}
// Every plain dropdown opens Roundly's own list on a computer (branded-select.ts).
installBrandedSelects()
startPickedChips()

import { dismissUndo } from './undo'

// The Back link above a page under More (navigation.ts moreBackLink). It sits outside #screen because every screen
// empties #screen when it draws.
const cameFrom: CameFrom = new Map()
let lastHash = window.location.hash
function showPageBack(screenEl: HTMLElement, previous: string): void {
  const leftFrom = rememberCameFrom(cameFrom, previous, current.screen, lastHash)
  lastHash = window.location.hash
  const link = !isWorker() && !isAdmin() ? moreBackLink(current.screen, leftFrom, window.location.hash) : null
  let bar = document.querySelector<HTMLElement>('#page-back')
  if (!link) { bar?.remove(); return }
  if (!bar) { bar = el('nav', { id: 'page-back', class: 'page-back', 'aria-label': 'Back' }); screenEl.before(bar) }
  bar.dataset.screen = current.screen
  bar.replaceChildren(el('a', { class: 'back-link', href: link.href }, link.label))
}

const presentOwnerPage = ownerPagePresentation(() => {
  const me = getMe()
  return me?.role === 'owner' && me.email && me.businessId ? `${me.email}:${me.businessId}` : ''
})

function route(): void {
  dismissUndo()
  const previous = current.screen
  current = parseHash()
  // The role decides where a hash may go: a worker lands on Today whatever
  // they tapped (the server would refuse the rest); an owner typing #/today
  // just gets the diary they know. Admins have Rounds and Partners only.
  if (isAdmin() && current.screen !== 'cleaners' && current.screen !== 'partners') {
    if (window.location.hash !== '#/rounds') window.location.hash = '#/rounds'
    current = { screen: 'cleaners' }
  } else if (isWorker() && current.screen !== 'today' && current.screen !== 'my-days' && current.screen !== 'account') {
    if (window.location.hash !== '#/today') window.location.hash = '#/today'
    current = { screen: 'today' }
  } else if (!isAdmin() && (current.screen === 'partners' || current.screen === 'cleaners')) {
    window.location.hash = '#/schedule'
    current = { screen: 'schedule' }
  } else if (!isWorker() && !ownerDoesJobs() && current.screen === 'my-days') {
    // My days is for workers and owners who do jobs; other owners use the diary.
    current = { screen: 'schedule' }
  } else if (current.screen === 'plan' && (!getMe()?.plan || inStoreApp())) {
    // Your plan is for the owner of a UK round with free months, and never inside the iPhone app (Apple 3.1.3).
    current = { screen: 'schedule' }
  } else if (!isWorker() && current.screen === 'today' && getMe()?.role !== 'owner') {
    // Owners have Today as a tab: who is out and their jobs (Liam, 2026-09-25).
    current = { screen: 'schedule' }
  }
  bumpRouteGen() // in-flight loads from the previous screen now can't render
  // The map page takes the whole window; leaving it must give the window back.
  if (previous === 'map' && current.screen !== 'map') unmountMap()
  renderNav()
  // The Rebook count is the SAME arithmetic the diary's round summary prints,
  // so it has to be as fresh as the screen you just landed on: without this,
  // walking to the diary showed a recalculated summary line beside a tab count
  // left over from the last data change, and the two numbers disagreed.
  if (!isWorker() && !isAdmin()) void refreshRebookBadge()
  const screen = document.querySelector<HTMLElement>('#screen')!
  presentOwnerPage(screen, current)
  showPageBack(screen, previous)
  screen.textContent = ''
  if (current.screen === 'customer') noteScreenBeforeCustomer(previous)
  // Back from a customer page or a day's map, the Diary keeps its day (the next-day rule, Liam, 20 Oct 2026).
  else if (current.screen === 'schedule') noteScreenBeforeDiary(previous)
  if (current.screen === 'more') mountMore()
  else if (current.screen === 'invoices') mountInvoices()
  else if (current.screen === 'old-customers') mountOldCustomers()
  else if (current.screen === 'grid') mountGrid()
  else if (current.screen === 'new-jobs') mountNewJobs()
  else if (current.screen === 'owed') mountOwed()
  else if (current.screen === 'rebook') mountRebook()
  else if (current.screen === 'chats') mountChats()
  else if (current.screen === 'get-set-up') mountGetSetUp()
  else if (current.screen === 'history') mountHistory()
  else if (current.screen === 'raise-prices') mountRaisePrices()
  else if (current.screen === 'analysis') mountAnalysis()
  else if (current.screen === 'today') mountToday()
  else if (current.screen === 'my-days') mountMyDays()
  else if (current.screen === 'business') mountBusiness()
  else if (current.screen === 'prices') mountPrices()
  else if (current.screen === 'account') mountAccount()
  else if (current.screen === 'plan') mountPlan()
  else if (current.screen === 'extra-features') mountExtraFeatures()
  else if (current.screen === 'your-messages') mountYourMessages()
  else if (current.screen === 'people') mountPeople()
  else if (current.screen === 'cleaners') mountCleaners()
  else if (current.screen === 'partners') {
    const gen = routeGen
    mountPartners(screen, (path) => api(path), () => gen === routeGen && isAdmin())
  } else if (current.screen === 'import') mountImport()
  else if (current.screen === 'bank') mountBank()
  else if (current.screen === 'start') mountStart()
  else if (current.screen === 'map' && current.date) mountMap(current.date)
  else if (current.screen === 'close-out' && current.date) mountCloseOut(current.date)
  else if (current.screen === 'customer' && current.id) mountCustomer(current.id)
  else mountSchedule()
  if (!document.body.classList.contains('owner-shell')) window.scrollTo(0, 0)
}

window.addEventListener('hashchange', route)

window.addEventListener('dropby:data-changed', () => {
  if (current.screen === 'grid') gridOnDataChanged()
  else if (current.screen === 'new-jobs') newJobsOnDataChanged()
  else if (current.screen === 'owed') owedOnDataChanged()
  else if (current.screen === 'rebook') rebookOnDataChanged()
  else if (current.screen === 'chats') chatsOnDataChanged()
  else if (current.screen === 'history') historyOnDataChanged()
  else if (current.screen === 'today') todayOnDataChanged()
  else if (current.screen === 'customer') customerOnDataChanged()
  else if (current.screen === 'raise-prices') raisePricesOnDataChanged()
  else if (current.screen === 'analysis') analysisOnDataChanged()
  else if (current.screen === 'close-out') closeOutOnDataChanged()
  else if (current.screen === 'business') businessOnDataChanged()
  else if (current.screen === 'prices') pricesOnDataChanged()
  else if (current.screen === 'extra-features') extraFeaturesOnDataChanged()
  else if (current.screen === 'people') peopleOnDataChanged()
  else if (current.screen === 'cleaners') cleanersOnDataChanged()
  else if (current.screen === 'partners') return // Read-only; its Refresh reloads the private queue.
  // More > Invoices reads its list again; never the Diary drawn over it (Sonnet).
  else if (current.screen === 'invoices') invoicesOnDataChanged()
  else if (current.screen === 'old-customers') oldCustomersOnDataChanged()
  // Import and the first step draw their own results; redrawing the diary
  // into them would replace "4 customers added" with the diary.
  else if (current.screen === 'import' || current.screen === 'start' || current.screen === 'bank' || current.screen === 'more') return
  else scheduleOnDataChanged()
})

// Coming back to Work, the Diary or Messages reads them again (Liam, 15 Oct 2026: "Work, the Diary and Messages pick up
// the other person's changes when you come back to them"); come-back.ts says when it waits.
readAgainOnComeBack({ here: () => current.screen === 'grid', busy: gridBusy, read: gridComeBack })
readAgainOnComeBack({ here: () => current.screen === 'rebook', busy: rebookBusy, read: rebookComeBack })
readAgainOnComeBack({ here: () => current.screen === 'schedule', busy: scheduleBusy, read: scheduleComeBack })

// A mutation that landed from the retry queue has no screen callback left to
// run (see api.ts), so anything the SERVER decided on its own is announced
// here instead — otherwise reconnecting silently changes money or adds a date.
window.addEventListener('dropby:mutation-notice', (e) => {
  const d = (e as CustomEvent).detail as {
    restored: boolean
    nextBooked: { date: string } | null
    replayedClean?: boolean
    removedBookings?: { date: string }[]
    row?: { date?: string; amount_pence?: number | null }
    money?: { kept_as_credit_pence?: number; receipt_pence?: number | null } | null
  }
  const lines = noticeLines(d, { money, shortDate, nextBookedMessage })
  if (lines.length) toast(lines.join('. '))
})

// Signal came back: re-READ, don't just flush writes. api.ts already retries
// the write queue on 'online'; without this the screen kept showing the
// offline banner until the user thought to tap a nav link (measured: 6s after
// reconnect the schedule was still empty). Routed through data-changed so the
// screen reloads IN PLACE — the user keeps their date window and scroll.
window.addEventListener('online', () => {
  window.dispatchEvent(new CustomEvent('dropby:data-changed'))
})
// Opened with no signal, and the server has now confirmed who this is (me.ts): every screen reads afresh, and the
// queue has started sending (docs/plans/offline/PLAN-v2.md §2).
window.addEventListener('dropby:signal-back', () => {
  window.dispatchEvent(new CustomEvent('dropby:data-changed'))
})

// ---- saving indicator (retry queue) -----------------------------------------
// The offline contract made visible: nothing is optimistic, so the indicator
// is the difference between "logged" and "not logged yet". Every coloured
// state stays until it is dealt with; '✓ Changes sent' (it claims nothing
// about unsubmitted drafts, only that nothing is owed to the server) shows
// for a few seconds and then fades, so it never sits over the page.
//
// - sending  → in flight now
// - queued   → waiting for signal; 'Stop retrying' (NOT 'cancel' — a lost
//   acknowledgement means the request may already be saved; stopping cannot
//   undo that, and the confirmation says so)
// - conflict → "reload screen" and "dismiss" are SEPARATE acts (codex
//   HIGH-6: reload-then-auto-dismiss erases the only record of the
//   mutation if the reload fails offline)
// - failed   → server rejected it (or the session is gone); red, dismiss only
// - storage  → queue won't survive a browser close; never silent

// A round switch reloads without saving anything. Keep its empty queue quiet until a real change.
let suppressSwitchClearChip = false
try { suppressSwitchClearChip = sessionStorage.getItem('roundly:round-switched') !== null } catch { /* storage may be off */ }
let chipOpen = false
let chipFade = 0
const CHIP_FADE_MS = 3000

// The sign-in page (nobody signed in) shows the saving box only when changes
// are waiting on this phone for their account; "Changes sent" means nothing yet.
let signinPage = false

function renderChip(): void {
  if (signinPage && waitingForOtherAccounts() === 0) {
    document.querySelector('#queue-chip')?.remove()
    return
  }
  const old = document.querySelector('#queue-chip')
  old?.remove()
  const q = getQueue()
  const persistNote = !queuePersistenceOk()
  if (q.length > 0) suppressSwitchClearChip = false
  if (suppressSwitchClearChip && q.length === 0 && !persistNote) return

  const signedOut = q.filter((m) => m.status === 'signed-out')

  const chip = el('div', { id: 'queue-chip' })
  const summary = el('button', { class: 'chip-summary', 'aria-expanded': String(chipOpen) })
  const head = chipSummary(q, persistNote)
  chip.classList.add(head.className)
  // The calm state draws a small pill inside a full-size tap target.
  if (head.className === 'chip-clear') summary.append(el('span', { class: 'chip-pill' }, head.text))
  else summary.textContent = head.text
  summary.addEventListener('click', () => {
    chipOpen = !chipOpen
    renderChip()
  })
  chip.append(summary)
  // Nothing waiting: "Changes sent" shows for a moment after the last save lands, then fades away so it never sits
  // on the page (2 Oct 2026). Every coloured state stays until it is dealt with; tapping where it was brings nothing
  // back, the next change does.
  if (chipFade) window.clearTimeout(chipFade)
  chipFade = 0
  if (head.className === 'chip-clear' && !chipOpen) chipFade = window.setTimeout(() => chip.classList.add('chip-faded'), CHIP_FADE_MS)

  if (chipOpen) {
    const list = el('div', { class: 'chip-list' })
    if (persistNote) {
      list.append(
        el(
          'div',
          { class: 'chip-item chip-warn' },
          "This browser can't store waiting changes: they will be lost if you close it.",
        ),
      )
    }
    if (q.length === 0) {
      // Accurate about what "nothing pending" means: only SUBMITTED changes
      // count. A half-typed cell or an unsubmitted form is a draft, not a
      // pending change — this must not read as "typed drafts are saved".
      list.append(
        el(
          'div',
          { class: 'chip-item' },
          'Nothing is waiting to save. Cell edits send when you press Enter or leave the cell; forms save with their Save or Add button. A typed draft is not saved until then.',
        ),
      )
    }
    if (signedOut.length > 0) {
      const warn = el('div', { class: 'chip-item chip-warn' })
      warn.append(
        el('span', {}, `${SIGNED_OUT_MESSAGE}. Nothing below is lost. It saves itself once you are back in.`),
      )
      const again = el('button', { class: 'chip-dismiss' }, 'Sign in again')
      again.addEventListener('click', () => window.location.reload())
      warn.append(again)
      list.append(warn)
    }
    // Said once, above the rows: why a change did not save and what to do, so the buttons read as plain choices.
    if (q.some((m) => m.status === 'conflict')) {
      list.append(el('div', { class: 'chip-item chip-explain' }, 'These did not save because the customer or visit changed first (on another screen, another phone or by an Undo). Notes and contact details still save when someone changed a different box; the same box, a price, Done, Paid or the day do not. Nothing else is lost. Reload to see the latest, then make the change again if it still needs doing.'))
    }
    for (const m of q) {
      const row = el('div', { class: 'chip-item' })
      const state = chipItemState(m)
      row.append(el('span', { class: 'chip-label' }, labelWords(m.label)), el('span', { class: 'chip-state' }, state))
      // Waiting behind a change that did not save, and never sent itself: safe to forget without a warning.
      if (m.status === 'queued' && m.error === BEHIND_EARLIER && !m.attempted) {
        const forget = el('button', { class: 'chip-dismiss' }, 'Forget this change')
        forget.addEventListener('click', () => cancelQueued(m.key))
        row.append(forget)
      } else if (m.status === 'queued') {
        const stop = el(
          'button',
          { class: 'chip-dismiss', title: 'Stop retrying. It may already be saved if the answer was lost.' },
          'Stop retrying',
        )
        stop.addEventListener('click', () => {
          // A queued request failed to be ACKNOWLEDGED, not to be sent: it
          // may have reached the server and saved before the connection
          // dropped. Stopping only ends the retries — the confirmation says
          // it cannot undo an already-saved change.
          if (window.confirm(`Stop retrying "${labelWords(m.label)}"? It may already have reached Roundly. Stopping cannot undo a change that saved, so check History before entering it again.`)) {
            cancelQueued(m.key)
          }
        })
        row.append(stop)
      }
      if (m.status === 'conflict' || m.status === 'failed') {
        if (m.status === 'conflict') {
          const reload = el('button', { class: 'chip-dismiss' }, 'Reload to see the latest')
          reload.addEventListener('click', () => route())
          row.append(reload)
        }
        const dis = el('button', { class: 'chip-dismiss' }, 'Forget this change')
        dis.addEventListener('click', () => dismiss(m.key))
        row.append(dis)
      }
      list.append(row)
    }
    chip.append(list)
  }
  document.body.append(chip)
}

onQueueChange(renderChip)

// ---- boot -------------------------------------------------------------------

const OWNER_SHELL = `
  <header class="topbar">
    <h1><a href="#/schedule"><img src="/roundly-mark.svg?v=b2" alt="" class="brand-mark" />Roundly</a></h1>
    ${ownerNavHTML()}
    <a href="#/chats" class="messages-chats-button" aria-label="Chats">Chats</a>
    <button type="button" class="help-button" id="help-button" aria-haspopup="dialog" hidden><span class="help-q" aria-hidden="true">?</span><span class="help-word">Help</span></button>
    <div class="account" id="account">
      <button type="button" class="account-button" id="account-button" aria-haspopup="menu" aria-expanded="false" aria-label="Account"><span class="account-initial" id="account-initial">?</span></button>
      <div class="account-menu" id="account-menu" role="menu" hidden>
        <p class="account-who" id="account-who"></p>
        <button type="button" class="account-item" id="account-photo" role="menuitem">Change picture</button>
        <button type="button" class="account-item" id="account-photo-remove" role="menuitem" hidden>Remove picture</button>
        <a class="account-logout" role="menuitem" href="#/account">Your account</a>
        <a class="account-logout" role="menuitem" href="${PRIVACY_URL}" target="_blank" rel="noopener">Privacy</a>
        <button type="button" class="account-logout" role="menuitem" data-logout>Log out</button>
        <button type="button" class="account-logout account-logout-all" role="menuitem" data-logout-all>Log out on every phone</button>
      </div>
    </div>
  </header>
  <main id="screen"></main>
`

// The worker's whole app is Today and My days — nothing to hide, nothing to read
// by accident. Every other route bounces back to Today (route()).
const WORKER_SHELL = `
  <header class="topbar">
    <h1><a href="#/today"><img src="/roundly-mark.svg?v=b2" alt="" class="brand-mark" />Roundly</a></h1>
    <nav>
      <a class="nav-item" data-screen="today" href="#/today">Today</a>
      <a class="nav-item" data-screen="my-days" href="#/my-days">My days</a>
    </nav>
    <button type="button" class="help-button" id="help-button" aria-haspopup="dialog" hidden><span class="help-q" aria-hidden="true">?</span><span class="help-word">Help</span></button>
    <div class="account" id="account">
      <button type="button" class="account-button" id="account-button" aria-haspopup="menu" aria-expanded="false" aria-label="Account"><span class="account-initial" id="account-initial">?</span></button>
      <div class="account-menu" id="account-menu" role="menu" hidden>
        <p class="account-who" id="account-who"></p>
        <button type="button" class="account-item" id="account-photo" role="menuitem">Change picture</button>
        <button type="button" class="account-item" id="account-photo-remove" role="menuitem" hidden>Remove picture</button>
        <button type="button" class="account-item" id="account-invoice" role="menuitem">My invoice details</button>
        <a class="account-logout" role="menuitem" href="#/account">Your account</a>
        <a class="account-logout" role="menuitem" href="${PRIVACY_URL}" target="_blank" rel="noopener">Privacy</a>
        <button type="button" class="account-logout" role="menuitem" data-logout>Log out</button>
        <button type="button" class="account-logout account-logout-all" role="menuitem" data-logout-all>Log out on every phone</button>
      </div>
    </div>
  </header>
  <main id="screen"></main>
`

// Liam approved separate Rounds and Partners admin pages (8 Oct 2026).
// Neither exposes a business's customer records or money; API scope stays unchanged.
const ADMIN_SHELL = `
  <header class="topbar">
    <h1><a href="#/rounds"><img src="/roundly-mark.svg?v=b2" alt="" class="brand-mark" />Roundly</a></h1>
    <nav class="admin-nav" aria-label="Administration">
      <a class="nav-item" data-screen="cleaners" href="#/rounds">Rounds</a>
      <a class="nav-item" data-screen="partners" href="#/partners">Partners</a>
    </nav>
    <div class="account" id="account">
      <button type="button" class="account-button" id="account-button" aria-haspopup="menu" aria-expanded="false" aria-label="Account"><span class="account-initial" id="account-initial">?</span></button>
      <div class="account-menu" id="account-menu" role="menu" hidden>
        <p class="account-who" id="account-who"></p>
        <a class="account-logout" role="menuitem" href="${PRIVACY_URL}" target="_blank" rel="noopener">Privacy</a>
        <button type="button" class="account-logout" role="menuitem" data-logout>Log out</button>
        <button type="button" class="account-logout account-logout-all" role="menuitem" data-logout-all>Log out on every phone</button>
      </div>
    </div>
  </header>
  <main id="screen"></main>
`

// The stored copy of the app (public/sw.js): registered on every open, not only when job reminders are turned on,
// so Roundly can open with no signal (docs/plans/offline/PLAN-v2.md §1). The phone is asked to keep Roundly's
// storage; it may say no, and Roundly still works, it just may have to download again after a clear-out.
function keepAppOnPhone(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined)
  void navigator.storage?.persist?.().catch(() => false)
}

let ownerNavObserver: ResizeObserver | null = null

async function boot(): Promise<void> {
  ownerNavObserver?.disconnect()
  ownerNavObserver = null
  document.body.style.removeProperty('--roundly-topbar-height')
  document.body.classList.remove('owner-shell')
  keepAppOnPhone()
  // The website's sign-up link says where this person came from (?from=first-100);
  // keep it before anything can move the address on (signup-from.ts, 0033).
  rememberSignupFrom()
  // One /api/me at boot decides which app this phone shows.
  await loadMe()
  // The round's clock and money sign, before any screen is drawn (round-config.ts, Step 1).
  setRoundConfig(getMe()?.round)
  // Photos and voice notes kept on the phone send themselves when there is
  // signal (media-store.ts); it needs to know who is signed in, so after /api/me.
  startAutoSend()
  // Pull down to refresh, in the home-screen app (pull-refresh.ts).
  enablePullToRefresh()
  // Every pop-up sheet: a round X to close, and on a phone dragged down to close (sheet-close.ts; Liam, 22 Oct 2026).
  watchSheets()
  keepEveryDialogAboveKeyboard()
  // A paused business is refused on every route, /api/me included, so it
  // never carries a role — check this before anything that branches on one.
  if (isSuspended()) {
    app.innerHTML = `
      <header class="topbar"><h1><img src="/roundly-mark.svg?v=b2" alt="" class="brand-mark" />Roundly</h1></header>
      <main id="screen"></main>
    `
    mountPaused()
    if ((getMe()?.rounds?.length ?? 0) > 1) {
      const header = app.querySelector('header')!
      const wrap = el('div', { class: 'account' })
      const button = el('button', { type: 'button', class: 'account-button', 'aria-label': 'Account', 'aria-expanded': 'false' }, (getMe()?.email ?? '?').charAt(0).toUpperCase())
      const menu = el('div', { class: 'account-menu', role: 'menu', hidden: '' })
      wrap.append(button, menu); header.append(wrap)
      bindRoundSwitcher(button, menu)
      button.addEventListener('click', () => { menu.hidden = !menu.hidden; button.setAttribute('aria-expanded', String(!menu.hidden)) })
    }
    renderChip()
    // A paused round sends no reminders (the Home Screen ones stop on the
    // server): the iPhone app's go too.
    void forgetAppReminders()
    void forgetAppCard()
    return
  }
  const screen = bootScreen()
  if (screen === 'signed-out') {
    // Nobody signed in (Roundly's own sign-in, 0025): the sign-in page, and
    // nothing else loads. Unsent changes stay held on the phone and go once
    // the same account is back in.
    // Its own full page: Roundly's dark background and logo, no app bar.
    signinPage = true
    document.body.classList.add('signin-body')
    app.innerHTML = `<main id="screen" class="signin-page"></main>`
    mountSignIn()
    renderChip()
    // Signed out, here or from another phone: no customer's name stays on
    // this phone's lock screen.
    void forgetAppReminders()
    void forgetAppCard()
    return
  }
  if (screen === 'unknown') {
    // The check itself failed (offline, expired session): WHO is signed in is
    // unknown, so NO shell may render — the owner layout over cached owner
    // data would show the wrong person's round on a shared phone. Nothing has
    // loaded and the queue is untouched; a reload retries from scratch.
    app.innerHTML = `
      <header class="topbar"><h1><img src="/roundly-mark.svg?v=b2" alt="" class="brand-mark" />Roundly</h1></header>
      <main id="screen">
        <p class="empty-note">Can't check who you are. Check your connection and try again.</p>
        <p><button id="boot-retry">Try again</button></p>
      </main>
    `
    document.querySelector('#boot-retry')?.addEventListener('click', () => window.location.reload())
    return
  }
  if (screen === 'not-a-user') {
    // Removed from the round: the same.
    void forgetAppReminders()
    void forgetAppCard()
    app.innerHTML = `
      <header class="topbar"><h1><img src="/roundly-mark.svg?v=b2" alt="" class="brand-mark" />Roundly</h1></header>
      <main id="screen"></main>
    `
    // A 'none' email may be offered "Set up your business" (signups open) —
    // everyone else is told to ask the owner, not Liam by name (multi-
    // business, 2026-09-22): the app no longer assumes Liam owns the round.
    if (getMe()?.canSignUp) {
      mountSetup()
    } else {
      const root = document.querySelector<HTMLElement>('#screen')!
      root.append(el('p', { class: 'empty-note' }, 'Ask the owner of your round to add your email in People.'))
      const out = el('button', { type: 'button', class: 'link-button' }, 'Sign out')
      out.addEventListener('click', () => void logOut())
      root.append(el('p', {}, out))
    }
    renderChip()
    return
  }
  document.body.classList.toggle('owner-shell', !isAdmin() && !isWorker())
  app.innerHTML = isAdmin() ? ADMIN_SHELL : isWorker() ? WORKER_SHELL : OWNER_SHELL
  // The Owed tab's sign is the round's (Step 2 round 2): the shell is written before the round is known.
  if (currencySymbol() === '$') app.querySelector('.nav-item[data-screen="owed"] .nav-icon path')?.setAttribute('d', DOLLAR_ICON)
  const ownerNav = document.querySelector<HTMLElement>('.owner-nav')
  if (ownerNav && typeof ResizeObserver !== 'undefined') {
    ownerNavObserver = new ResizeObserver(renderNav)
    ownerNavObserver.observe(ownerNav)
    const topbar = document.querySelector<HTMLElement>('.topbar')
    if (topbar) ownerNavObserver.observe(topbar)
    for (const link of ownerNav.querySelectorAll('.nav-item')) ownerNavObserver.observe(link)
  }
  // Help (help.ts): owners and workers, top right beside the account circle.
  bindHelpButton()
  // A paused or view-only round says so under the top bar (billing stage 6).
  if (!isAdmin()) watchAccessLine()
  // Owners open on Schedule (Liam, 2026-09-25: "I like the fact it opens on
  // schedule still"); Today is the next tab, for every owner.
  // Account: a circle with the signed-in person's initial, top right. Tap it
  // for who is signed in and Log out — the owner tests both logins on one
  // phone and could not tell which one he was in.
  const me = getMe()
  const who = me?.workerName || (me?.email ? me.email.split('@')[0] : '')
  const initial = document.querySelector<HTMLElement>('#account-initial')
  const whoLine = document.querySelector<HTMLElement>('#account-who')
  const button = document.querySelector<HTMLButtonElement>('#account-button')
  const menu = document.querySelector<HTMLElement>('#account-menu')
  // My working days is under More, with the rest of the round (Liam, 9 Oct 2026), not in this menu.
  if (initial && who) initial.textContent = who.charAt(0).toUpperCase()
  // Their picture, or the business's stand-in (Dropby's logo), in place of
  // the initial. If it fails to load, the initial comes back.
  if (initial && me?.avatar) {
    const img = document.createElement('img')
    img.className = 'account-photo'
    img.alt = ''
    img.src = me.avatar
    img.addEventListener('error', () => img.replaceWith(initial))
    initial.replaceWith(img)
  }
  const roleTag = me?.role === 'worker' ? ' · worker' : me?.role === 'admin' ? ' · admin' : ''
  if (whoLine) {
    whoLine.textContent = ''
    whoLine.append(document.createTextNode(who ? `${who}${roleTag}` : 'Signed in'))
    if (me?.email) { whoLine.append(document.createElement('br')); whoLine.append(document.createTextNode(me.email)) }
    // The free three months (0028). Not inside the iPhone app: Apple's rules
    // for a free app that works with a paid website keep all talk of paying
    // on the website.
    if (me?.trialEndsOn && !inStoreApp()) {
      const left = Math.round((Date.parse(`${me.trialEndsOn}T12:00:00Z`) - Date.parse(`${todayISO()}T12:00:00Z`)) / 86_400_000)
      if (left >= 0) whoLine.append(el('span', { class: 'account-trial' }, left === 0 ? 'Free trial: last day today' : `Free trial: ${left} ${left === 1 ? 'day' : 'days'} left`))
    }
  }
  if (button && menu) {
    bindRoundSwitcher(button, menu)
    const close = (): void => { menu.hidden = true; button.setAttribute('aria-expanded', 'false') }
    button.addEventListener('click', (e) => { e.stopPropagation(); menu.hidden = !menu.hidden; button.setAttribute('aria-expanded', menu.hidden ? 'false' : 'true') })
    document.addEventListener('click', (e) => { if (!menu.hidden && !menu.contains(e.target as Node)) close() })
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close() })
    window.addEventListener('hashchange', close)
    document.querySelector<HTMLButtonElement>('[data-logout]')?.addEventListener('click', () => { close(); void logOut() })
    document.querySelector<HTMLButtonElement>('[data-logout-all]')?.addEventListener('click', () => { close(); void logOut(true) })
    const photo = document.querySelector<HTMLButtonElement>('#account-photo')
    const photoRemove = document.querySelector<HTMLButtonElement>('#account-photo-remove')
    photo?.addEventListener('click', () => { close(); chooseAndUploadAvatar() })
    // A worker's own details for the invoice they send when they finish a day (0064).
    document.querySelector<HTMLButtonElement>('#account-invoice')?.addEventListener('click', () => { close(); void import('./day-close').then(({ openInvoiceDetails }) => openInvoiceDetails()) })
    if (photoRemove) {
      photoRemove.hidden = !me?.hasOwnPhoto
      photoRemove.addEventListener('click', () => { close(); void removeAvatar() })
    }
  }
  // An owner opening Roundly with no screen asked for lands on Today whenever today has work (open-on-today.ts);
  // otherwise the Diary, as before. Put in the address without a hashchange, so the screen is drawn once.
  const noScreen = !window.location.hash || window.location.hash === '#' || window.location.hash === '#/'
  if (noScreen && getMe()?.role === 'owner' && workToday()) history.replaceState(history.state, '', '#/today')
  route()
  renderChip()
  // Job reminders kept by Roundly's iPhone app, in step with the round.
  if (!isAdmin()) watchAppReminders()
  // The lock screen card in Roundly's iPhone app, in step with the person's own day (lock-card.ts).
  if (!isAdmin()) watchAppCard()
  // Calls inside Roundly: owners of the business calling is set up for.
  if (!isAdmin()) watchCalls()
  if (isWorker() || isAdmin()) return
  // The Owed count pops when it rises, only after reading it again (owed-badge.ts; tidy-up item 2, 20 Oct 2026).
  onOwedBadgeReread(refreshOwedBadge)
  void refreshOwedBadge()
  // Quote alerts: the number on More of customers waiting for your answer (enquiry-alerts.ts).
  watchEnquiryBadge()
  window.addEventListener('dropby:data-changed', () => void refreshOwedBadge())
  window.addEventListener('dropby:saved', scheduleOwedBadgeRefresh)
  window.addEventListener('dropby:data-changed', () => void refreshRebookBadge())
  // Every save anywhere, the Diary's included (Mark confirmed, Move, a new time, Cancel, placing someone), can change
  // who has a message to send. The Messages count follows straight away, as Owed's does, not on the next tab change
  // (Diary and Messages together, Liam 8 Oct 2026).
  window.addEventListener('dropby:saved', scheduleRebookBadgeRefresh)
}

let rebookBadgeTimer: ReturnType<typeof setTimeout> | null = null
function scheduleRebookBadgeRefresh(): void {
  if (rebookBadgeTimer !== null) clearTimeout(rebookBadgeTimer)
  rebookBadgeTimer = setTimeout(() => { rebookBadgeTimer = null; void refreshRebookBadge() }, 300)
}

const app = document.querySelector<HTMLDivElement>('#app')!
void boot()

// The owed count rides in the nav so the receivable is visible from every
// screen — a number you have to navigate to is a number nobody looks at.
// Workers are told neither: both endpoints would only refuse them.
let owedBadgeTimer: ReturnType<typeof setTimeout> | null = null

function scheduleOwedBadgeRefresh(): void {
  if (isWorker()) return
  nextOwedBadgeSequence()
  if (owedBadgeTimer !== null) clearTimeout(owedBadgeTimer)
  owedBadgeTimer = setTimeout(() => {
    owedBadgeTimer = null
    void refreshOwedBadge()
  }, 300)
}

async function refreshOwedBadge(): Promise<void> {
  if (isWorker()) return
  if (!document.querySelector('#owed-badge')) return
  const sequence = nextOwedBadgeSequence()
  try {
    const d = await api<OwedPayload>('/api/owed', 'owed')
    setOwedBadge(d, todayISO(), sequence)
  } catch {
    // offline — leave whatever the last known count was rather than blanking
    // it, which would read as "nothing owed"
  }
}

// The Rebook count (refreshRebookBadge, from screens/rebook) also registers
// in boot() only — a worker's phone must not be polling owner endpoints that
// will only refuse it.

import './lead-conversion.css'
